import { AiMark, type DetailField, DetailSections, DerivedValueCell, type FieldEditorDef, InlineMessage } from '@olytic/foundation';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import {
  type ApiDerivedValue,
  type DerivableObject,
  type DerivedField,
  fetchFields,
  fetchValues,
  QUEUED_HINT,
  rawDerived,
  rerunField,
  saveValue,
  toDerivedValue,
} from './derivedApi';

const FIELD_PREFIX = 'derived:';

/** The inline editor for a value of this type. Clearing a value hands it back to the AI. */
function editorFor(field: DerivedField): FieldEditorDef<never> {
  switch (field.output_type) {
    case 'number':
    case 'currency':
      return { kind: 'number', nullable: true };
    case 'date':
      return { kind: 'date', nullable: true };
    case 'boolean':
      return { kind: 'select', nullable: true, options: [{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }] };
    case 'picklist':
      return { kind: 'select', nullable: true, options: field.options.map((o) => ({ value: o, label: o })) };
    default:
      return { kind: 'text', nullable: true, maxLength: field.output_type === 'text' ? 500 : 10_000 };
  }
}

/**
 * The record page's "From history" section: the object's fields from history
 * with this record's values and where they came from. A person can correct a
 * value in place; clearing it lets the AI fill it again.
 */
export function FromHistorySection({
  object, recordId, onNavigate,
}: { object: DerivableObject; recordId: string; onNavigate: (href: string) => void }): JSX.Element | null {
  const [fields, setFields] = useState<DerivedField[]>([]);
  const [values, setValues] = useState<Map<string, ApiDerivedValue>>(new Map());
  const [version, setVersion] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    fetchFields(authenticatedFetch, object, { signal })
      .then(async (found) => {
        const shown = found.filter((f) => f.status === 'active');
        const loaded = shown.length ? await fetchValues(authenticatedFetch, object, [recordId], { signal }) : [];
        if (signal.aborted) return;
        setFields(shown);
        setValues(new Map(loaded.map((v) => [v.fieldId, v])));
      })
      .catch(() => { /* No section when history can't load. */ });
    return () => controller.abort();
  }, [object, recordId, version]);

  const rerun = useCallback(async (field: DerivedField) => {
    await rerunField(authenticatedFetch, field.id, [recordId]);
    setVersion((n) => n + 1);
  }, [recordId]);

  const detailFields = useMemo<DetailField[]>(
    () =>
      fields.map((field) => {
        const value = values.get(field.id);
        return {
          key: `${FIELD_PREFIX}${field.id}`,
          label: field.label,
          wide: field.output_type === 'long_text',
          value: value ? (
            <DerivedValueCell
              variant="field"
              value={toDerivedValue(field, value)}
              label={field.instruction}
              pendingHint={QUEUED_HINT}
              onNavigate={onNavigate}
              onRerun={() => void rerun(field)}
            />
          ) : null,
          editor: editorFor(field),
          raw: value?.edited || value?.state === 'done' ? rawDerived(field.output_type, value.value) : '',
        };
      }),
    [fields, values, onNavigate, rerun],
  );

  if (fields.length === 0) return null;
  return (
    <>
      {error ? <InlineMessage tone="error" role="alert" className="mb-3">{error}</InlineMessage> : null}
      <DetailSections
        sections={[{ key: 'from-history', label: <span className="inline-flex items-center gap-1"><AiMark /> From history</span>, fields: detailFields }]}
        onFieldSave={async (key, raw) => {
          const fieldId = key.slice(FIELD_PREFIX.length);
          setError(null);
          try {
            const saved = await saveValue(authenticatedFetch, fieldId, recordId, raw === '' ? null : raw);
            setValues((current) => new Map(current).set(fieldId, saved));
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not save the value.');
            throw e;
          }
        }}
      />
    </>
  );
}
